import React, { useState, useEffect } from "react";
import {
  useGetEmployeesForResetQuery,
  useResetEmployeePasswordMutation,
} from "state/api";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import { SearchOutlined, LockResetOutlined } from "@mui/icons-material";

const ResetPassword = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch employees for reset
  const { data: response, isLoading, error } = useGetEmployeesForResetQuery();
  const employees = response?.data || [];

  // Reset password mutation
  const [resetPassword, { isLoading: isResetting }] =
    useResetEmployeePasswordMutation();

  // Handle search filtering
  useEffect(() => {
    if (searchTerm) {
      const filtered = employees.filter(
        (emp) =>
          emp.NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.EmpNo?.toString().includes(searchTerm)
      );
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees(employees);
    }
  }, [searchTerm, employees]);

  // Handle employee selection
  const handleOpenDialog = (employee) => {
    setSelectedEmployee(employee);
    setNewPassword("");
    setConfirmPassword("");
    setErrorMessage("");
    setOpenDialog(true);
  };

  // Handle password reset
  const handleResetPassword = async () => {
    // Validation
    if (!newPassword || !confirmPassword) {
      setErrorMessage("Both password fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage("Password must be at least 6 characters long");
      return;
    }

    try {
      const result = await resetPassword({
        EmpNo: selectedEmployee.EmpNo,
        newpassword: newPassword,
      }).unwrap();

      if (result.success) {
        setSuccessMessage(
          `Password reset successfully for ${selectedEmployee.NAME}!`
        );
        setOpenDialog(false);
        setSelectedEmployee(null);
        setNewPassword("");
        setConfirmPassword("");
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (err) {
      setErrorMessage(
        err?.data?.message || "Failed to reset password. Please try again."
      );
    }
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEmployee(null);
    setNewPassword("");
    setConfirmPassword("");
    setErrorMessage("");
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress sx={{ color: "#3b82f6" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error?.data?.message || "Failed to load employees. Please refresh."}
      </Alert>
    );
  }

  return (
    <Box sx={{ color: "#f1f5f9" }}>
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          fontWeight: 700,
          color: "white",
        }}
      >
        Reset Employee Password
      </Typography>

      {successMessage && (
        <Alert
          severity="success"
          onClose={() => setSuccessMessage("")}
          sx={{ mb: 2 }}
        >
          {successMessage}
        </Alert>
      )}

      {/* Search Section */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 2,
          alignItems: "center",
        }}
      >
        <SearchOutlined sx={{ color: "#3b82f6", fontSize: 28 }} />
        <TextField
          placeholder="Search by employee name or number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              color: "white",
              backgroundColor: "rgba(30, 41, 59, 0.8)",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
              "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiOutlinedInput-input::placeholder": {
              color: "rgba(255,255,255,0.5)",
              opacity: 1,
            },
          }}
        />
      </Box>

      {/* Employees Table */}
      {filteredEmployees.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{
            background: "#1e293b",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(59, 130, 246, 0.2)",
            border: "1px solid rgba(59, 130, 246, 0.2)",
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background:
                    "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)",
                  "& .MuiTableCell-head": {
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                  },
                }}
              >
                <TableCell>Employee No.</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>MGCD</TableCell>
                <TableCell>HeadQuarter</TableCell>
                <TableCell>State</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmployees.map((employee, index) => (
                <TableRow
                  key={employee.EmpNo}
                  sx={{
                    backgroundColor:
                      index % 2 === 0
                        ? "rgba(59, 130, 246, 0.05)"
                        : "transparent",
                    borderBottom: "1px solid rgba(59, 130, 246, 0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(59, 130, 246, 0.1)",
                      transition: "all 0.3s ease",
                    },
                  }}
                >
                  <TableCell sx={{ color: "#f1f5f9", fontWeight: 600 }}>
                    {employee.EmpNo}
                  </TableCell>
                  <TableCell sx={{ color: "#f1f5f9" }}>
                    {employee.NAME}
                  </TableCell>
                  <TableCell sx={{ color: "#60a5fa", fontWeight: 600 }}>
                    {employee.MgCd}
                  </TableCell>
                  <TableCell sx={{ color: "#f1f5f9" }}>
                    {employee.HeadQrt}
                  </TableCell>
                  <TableCell sx={{ color: "#94a3b8" }}>
                    {employee.StateCd}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<LockResetOutlined />}
                      onClick={() => handleOpenDialog(employee)}
                      sx={{
                        background: "#ff9800",
                        color: "white",
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": {
                          background: "#f57c00",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Reset
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ color: "#94a3b8" }}>
            {searchTerm
              ? "No employees found matching your search"
              : "No employees available"}
          </Typography>
        </Box>
      )}

      {/* Reset Password Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: "#1e293b",
            color: "white",
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: 700, color: "white", fontSize: "1.3rem" }}
        >
          Reset Password
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Employee Info */}
          <Box
            sx={{
              background: "rgba(59, 130, 246, 0.1)",
              border: "1px solid rgba(59, 130, 246, 0.3)",
              borderRadius: 1,
              p: 2,
              mb: 3,
            }}
          >
            <Typography variant="body2" sx={{ color: "#94a3b8", mb: 0.5 }}>
              <strong style={{ color: "#f1f5f9" }}>Name:</strong>{" "}
              {selectedEmployee?.NAME}
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8", mb: 0.5 }}>
              <strong style={{ color: "#f1f5f9" }}>Employee No:</strong>{" "}
              {selectedEmployee?.EmpNo}
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              <strong style={{ color: "#f1f5f9" }}>MGCD:</strong>{" "}
              {selectedEmployee?.MgCd}
            </Typography>
          </Box>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {/* Password Fields */}
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Min 6 characters"
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                color: "white",
                "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
                "&:hover fieldset": { borderColor: "#3b82f6" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
              },
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiInputLabel-root": { color: "#94a3b8" },
            }}
            disabled={isResetting}
          />
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm password"
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "white",
                "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
                "&:hover fieldset": { borderColor: "#3b82f6" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
              },
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiInputLabel-root": { color: "#94a3b8" },
            }}
            disabled={isResetting}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: "#94a3b8",
              "&:hover": { background: "rgba(148, 163, 184, 0.1)" },
            }}
            disabled={isResetting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            sx={{
              background: "#10b981",
              color: "white",
              fontWeight: 600,
              "&:hover": {
                background: "#059669",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
              },
              transition: "all 0.3s ease",
            }}
            disabled={isResetting}
          >
            {isResetting ? <CircularProgress size={24} /> : "Confirm Reset"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResetPassword;
